import {
  Toast,
  ToastBody,
  Toaster,
  ToastTitle,
  useId,
  useToastController,
  type ToastIntent,
} from '@fluentui/react-components'
import React, { createContext, useCallback, useContext, type ReactNode } from 'react'

import type { ProblemDetails } from '../data/types/ProblemDetails'

// ── Types ──

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'default'

interface ToastApi {
  show: (message: string | ReactNode, type?: ToastType, timeout?: number) => void
  showProblemDetails: (problemDetails: ProblemDetails, timeout?: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

// ── Provider ──

export function FluentToastProvider({ children }: { children: ReactNode }) {
  const toasterId = useId('global-toaster')
  const { dispatchToast } = useToastController(toasterId)

  const intentMap: Record<ToastType, ToastIntent> = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info',
    default: 'info',
  }

  const show = useCallback(
    (message: string | ReactNode, type: ToastType = 'info', timeout = 4000) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{typeof message === 'string' ? message : 'Notification'}</ToastTitle>
          {typeof message !== 'string' && <ToastBody>{message}</ToastBody>}
        </Toast>,
        {
          position: 'top-end',
          intent: intentMap[type],
          timeout: timeout === 0 ? -1 : timeout,
        },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchToast],
  )

  const showProblemDetails = useCallback(
    (problemDetails: ProblemDetails, timeout = 8000) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{problemDetails.title}</ToastTitle>
          <ToastBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>{problemDetails.detail}</span>
              {problemDetails.errors && Object.keys(problemDetails.errors).length > 0 && (
                <ul style={{ margin: '4px 0', paddingLeft: '16px', fontSize: '12px' }}>
                  {Object.entries(problemDetails.errors).map(([field, messages]) =>
                    messages.map((msg, i) => (
                      <li key={`${field}-${i}`}>
                        <strong>{field}:</strong> {msg}
                      </li>
                    )),
                  )}
                </ul>
              )}
              {problemDetails.traceId && (
                <span style={{ fontSize: '11px', opacity: 0.7 }}>
                  Trace: {problemDetails.traceId}
                </span>
              )}
            </div>
          </ToastBody>
        </Toast>,
        {
          position: 'top-end',
          intent: 'error',
          timeout: timeout === 0 ? -1 : timeout,
        },
      )
    },
    [dispatchToast],
  )

  return (
    <ToastContext.Provider value={{ show, showProblemDetails }}>
      {children}
      <Toaster toasterId={toasterId} />
    </ToastContext.Provider>
  )
}

// ── Hook ──

export function useFluentToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useFluentToast must be used within FluentToastProvider')
  return ctx
}

// ── Imperative API (for non-component code like axios interceptors) ──

let globalToastApi: ToastApi | null = null

export function registerGlobalToast(api: ToastApi) {
  globalToastApi = api
}

/**
 * Drop-in replacement for the old showToast function.
 * Works from anywhere — components, interceptors, services.
 */
export const showToast = (
  message: string | React.ReactNode,
  type: ToastType = 'info',
  autoClose?: number | false,
) => {
  if (globalToastApi) {
    globalToastApi.show(
      message,
      type,
      typeof autoClose === 'number' ? autoClose : autoClose === false ? 0 : undefined,
    )
  } else {
    // Fallback before provider is mounted
    console.warn('[Toast] Provider not ready, message:', message)
  }
}

export const showProblemDetailsToast = (
  problemDetails: ProblemDetails,
  autoClose: number | false = 8000,
) => {
  if (globalToastApi) {
    globalToastApi.showProblemDetails(problemDetails, typeof autoClose === 'number' ? autoClose : 0)
  } else {
    console.warn('[Toast] Provider not ready, problem:', problemDetails.title)
  }
}
